import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade7Zhuyin.css";

function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade7Zhuyin
 *  - Very advanced usage: reading advanced texts, possibly literary excerpts, specialized vocab
 *  - Single quiz pool => 25 Q
 */

const advancedLitNotes = (
  <div className="g7z-lit-notes">
    <h4>Literary Excerpts & Specialized Topics in Zhuyin</h4>
    <p>
      By Grade 7, you may see literary passages or specialized subject readings (history, science) with Zhuyin. 
      This can include older or more formal language, idioms, and rhetorical expressions.
    </p>
    <ul>
      <li>Sample excerpt from a classic text with Zhuyin annotations.</li>
      <li>Focus on understanding context, older grammar forms, or classical references.</li>
      <li>May see specialized terms for geography, biology, etc., all spelled in Zhuyin.</li>
    </ul>
  </div>
);

const zhuyinQuestions = [
  {
    question: "At Grade 7, literary excerpts in Zhuyin might contain older grammar, e.g. ㄑㄧˋ ㄕˋ => 'abandon the vow'? This indicates?",
    correctAnswer: "Exposure to more formal or archaic expressions spelled with Zhuyin",
    options: [
      "No difference from daily speech",
      "We never see older grammar at this level",
      "More formal expressions, spelled in Zhuyin for study"
    ]
  },
  {
    question: "Specialized vocabulary in science, e.g. ㄉㄨˊ ㄒㄧㄥˊ => 'toxic star' or 'venomous shape'? Might be a biology term. You should?",
    correctAnswer: "Check context or footnotes to interpret specialized terms",
    options: [
      "Ignore specialized words entirely",
      "It’s guaranteed to be a star in space",
      "Check context or footnotes for correct meaning"
    ]
  },
  {
    question: "Literary text might mention ㄩㄢ ㄉㄞˋ => 'Yuan Dynasty.' This is a historical reference. Why use Zhuyin here?",
    correctAnswer: "To guide pronunciation & meaning for students new to the term",
    options: [
      "We skip historical terms in Grade 7",
      "It’s purely random with no reason",
      "Zhuyin helps with correct reading of historical names"
    ]
  },
  {
    question: "An excerpt may use ㄉㄚˋ ㄍㄨㄛˊ ㄓㄜˋ => 'great kingdom policy'? Possibly a classical phrase. We interpret it by...?",
    correctAnswer: "Looking at the text’s context or teacher’s notes for classical meaning",
    options: [
      "Guessing modern usage only",
      "Discarding it as old text",
      "Checking context or teacher’s notes"
    ]
  },
  {
    question: "If you see idioms like ㄒㄧㄣ ㄒㄧㄤ ㄕˋ ㄔㄥ => 'If one thinks it, it can be done,' a rhetorical flourish. This shows?",
    correctAnswer: "Zhuyin can annotate even classical or idiomatic lines for modern learners",
    options: [
      "Idioms are not allowed in Zhuyin",
      "No rhetorical usage in Grade 7",
      "Zhuyin can annotate classical/idiomatic lines"
    ]
  },
  {
    question: "Reading an excerpt about ㄨㄣˇ ㄉㄧㄥˋ => 'stable, settled' might mention older synonyms. This fosters?",
    correctAnswer: "Vocabulary expansion into more formal or literary terms",
    options: [
      "No learning, it’s irrelevant",
      "Confusion only, skip them",
      "Broadening vocabulary with formal terms"
    ]
  },
  {
    question: "If the text is about geography: ㄌㄧㄢˊ ㄕㄢ ㄑㄩ => 'connected mountain areas.' We might see measure words, older place names. The best approach is?",
    correctAnswer: "Consult references or a map to understand these specialized terms",
    options: [
      "Ignore them, too advanced",
      "Change them to pinyin instantly",
      "Consult references for specialized usage"
    ]
  },
  {
    question: "A literary line: ㄨㄟˊ ㄧˇ ㄍㄨㄟˋ => 'regarding something as precious'? If you mis-tone ㄨㄟˊ => it might become ㄨㄟˇ, changing meaning to 'tail' in some contexts. True?",
    correctAnswer: "Yes, tone slip can drastically alter meaning even in literary texts",
    options: [
      "No difference, they’re the same",
      "Yes, tone changes meaning drastically",
      "No advanced usage of tone here"
    ]
  },
  {
    question: "Some text might mention ㄌㄧˇ ㄩㄥˋ => 'ritual usage' (禮用) in a historical context. Understanding cultural or historical references is important because?",
    correctAnswer: "It clarifies why certain terms are used & how they relate to tradition",
    options: [
      "We skip culture in Grade 7",
      "No context needed, just read letters",
      "Understanding tradition & cultural references is key"
    ]
  },
  {
    question: "A specialized biology term: ㄕㄥ ㄓㄨˋ => 'to grow/live'? Could be part of ecology content. We verify meaning by?",
    correctAnswer: "Looking up the full phrase or sentence. Maybe it's '生住' or '生著' depending on the text",
    options: [
      "Guess it means 'to die' ironically",
      "Ignore any partial clue",
      "Check the full phrase in context or dictionary"
    ]
  },
  {
    question: "If a text uses ㄕㄥ ㄌㄧˋ => 'victory' or 'to win,' it might appear in historical accounts. Tone mismatch could become ㄕㄤ ˋ ㄌㄧ => nonsense. So?",
    correctAnswer: "Again, we see how crucial tone is for advanced specialized words",
    options: [
      "It’s optional at advanced levels",
      "No difference in meaning",
      "We must keep correct tone"
    ]
  },
  {
    question: "A short literary commentary might end with ㄐㄧㄝˊ ㄌㄨㄣˋ => 'conclusion' or 'judgment.' This is typical for advanced texts to sum up. True or false?",
    correctAnswer: "True, advanced texts often have concluding remarks spelled in Zhuyin",
    options: [
      "False, we never conclude",
      "True, concluding remarks are common",
      "No usage for endings"
    ]
  },
  {
    question: "Reading older text with ㄔㄣˊ ㄗㄨ => 'minister ancestor'? Possibly referencing a lineage. This shows?",
    correctAnswer: "Historical or genealogical references can appear in advanced reading",
    options: [
      "We only see modern slang",
      "No genealogical mention in Grade 7",
      "Yes, historical contexts appear"
    ]
  },
  {
    question: "In specialized science text: ㄆㄢˋ ㄉㄨㄢˋ => 'pànduàn' (to judge or conclude). This might appear in experiments or research. Are we comfortable reading that in Zhuyin now?",
    correctAnswer: "Yes, Grade 7 coverage includes specialized vocab in various subjects",
    options: [
      "No, only daily conversation words",
      "Yes, we see advanced & specialized terms in Zhuyin",
      "We must skip science words"
    ]
  },
  {
    question: "Some text might mention ㄒㄧㄣ ㄍㄡ ㄓ ㄩ => 'newly formed language'? If it's cryptic, context or teacher’s note is needed. This fosters?",
    correctAnswer: "Independent research & reference checking for advanced vocabulary",
    options: [
      "We ignore cryptic references",
      "No teacher’s notes allowed in advanced texts",
      "We do independent reference checks"
    ]
  },
  {
    question: "Archaic forms: ㄒㄧ ㄢ => '昔焉' or 'formerly, in the past'? This is classical usage. Should we...?",
    correctAnswer: "Learn how it relates to modern usage, bridging classical and contemporary language",
    options: [
      "Ignore classical usage",
      "Yes, bridging classical & modern helps deeper understanding",
      "We only read modern slang"
    ]
  },
  {
    question: "If you see a note referencing ㄨㄣˇ ㄏㄨㄚˋ => 'culture' or 'civilization,' in a historical passage, it's important to note tone. Why?",
    correctAnswer: "Mixing ㄨㄣˇ vs. ㄨㄣˋ changes meaning (文 vs. 問?), so be precise",
    options: [
      "No difference in those syllables",
      "Yes, 'wěn' vs. 'wèn' are distinct",
      "We always guess"
    ]
  },
  {
    question: "A specialized geography term might be ㄏㄨˇ ㄏㄜˊ => 'Tiger River'? Could be a local name or figurative. We confirm by?",
    correctAnswer: "Seeing if it’s a real place or symbolic phrase in the text",
    options: [
      "We ignore place names in Grade 7",
      "All rivers have the same name",
      "Check context for real location or figurative usage"
    ]
  },
  {
    question: "Literary texts might use ㄔㄨㄢˊ ㄐㄧ => 'to pass down records' (傳記). This is read 'zhuàn jì' or 'chuán jì'? Tones matter. So we do?",
    correctAnswer: "Check the context: 傳 can be chuán or zhuàn, different meaning (to pass down vs. biography)",
    options: [
      "They are identical, no difference",
      "We ignore subtle tone changes",
      "We confirm context: biography vs. passing down"
    ]
  },
  {
    question: "At Grade 7, practicing these literary and specialized passages with Zhuyin builds readiness for older texts or subject reading. True?",
    correctAnswer: "True, it’s a stepping stone to full classical Chinese or specialized fields",
    options: [
      "False, no stepping stone needed",
      "True, it readies you for older texts/fields",
      "We only focus on conversation"
    ]
  },
  {
    question: "Finally, the best tip: keep reading widely—essays, short classics, specialized articles—and consult references for new terms. Helps with...?",
    correctAnswer: "Vocabulary growth, cultural literacy, and advanced reading skills",
    options: [
      "Only a waste of time",
      "No effect on literacy",
      "Improves vocab, culture knowledge, advanced reading"
    ]
  }
];

export default function Grade7Zhuyin() {
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 mins

  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Quick Quiz
  const generateQuiz = () => {
    const q = zhuyinQuestions[Math.floor(Math.random() * zhuyinQuestions.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options])
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const handleAnswerClick = (ans) => {
    setSelectedAnswer(ans);
    if (quiz && ans === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Final Quiz
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g7chi-page">
      <h1 className="g7chi-title">Grade 7 Zhuyin: Literary & Specialized Texts</h1>
      <p className="g7chi-description">
        Explore advanced or literary passages annotated with Zhuyin, including classical references and specialized subjects.
      </p>

      <div className="g7chi-lesson-content">
        <h2 className="g7chi-lesson-title">注音: Literary Excerpts & Specialized Vocabulary</h2>
        <p className="g7chi-lesson-description">
          By Grade 7, you might read historical, scientific, or literary texts all in Zhuyin. This expands your range of vocabulary and cultural understanding.
        </p>
        <p className="g7chi-lesson-example">
          <strong>Example:</strong> ㄕㄣˋ ㄓ ㄨㄟˊ ㄍㄨㄟˋ = “Consider it extremely valuable.”
        </p>
        {advancedLitNotes}
      </div>

      <div className="g7chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g7chi-quick-quiz-container">
          <h2 className="g7chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g7chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g7chi-quiz-question">{quiz.question}</p>
              <div className="g7chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g7chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g7chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g7chi-final-quiz-container">
          <h2 className="g7chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g7chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g7chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>
                Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
              </p>
              <p className="g7chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g7chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g7chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
